import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { scrollToId } from "@/components/site/Reveal";
import { useCart } from "@/context/CartContext";
import { AuthMenu } from "@/components/site/AuthMenu";

const LINKS = [
  { id: "menu", label: "Menu" },
  { id: "builder", label: "Stack Your Fuel" },
  { id: "vending", label: "Vending" },
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-noir/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => scrollToId("home")} className="text-left leading-none">
          <span className="block font-display text-xl font-extrabold uppercase tracking-tight text-white">
            Protein<span className="text-amber-brand">Cafe</span>
          </span>
          <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">The Fuel Lounge&trade;</span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium tracking-wide text-white/80 hover:text-amber-brand transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-amber-brand hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => go("menu")}
            className="rounded-full bg-[#DE9A2C] hover:bg-[#c9861f] text-[#17120C] text-sm font-semibold px-6 py-2.5 transition-colors"
          >
            View Menu
          </button>
          <button data-testid="nav-cart" onClick={() => setCartOpen(true)} className="relative text-white hover:text-amber-brand transition-colors">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-brand text-[#17120C] text-[10px] font-bold">{count}</span>
            )}
          </button>
          <AuthMenu />
        </div>

        <div className="md:hidden flex items-center gap-4">
          <button data-testid="nav-cart-mobile" onClick={() => setCartOpen(true)} className="relative text-white">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-brand text-[#17120C] text-[10px] font-bold">{count}</span>
            )}
          </button>
          <button data-testid="nav-mobile-toggle" className="text-white" onClick={() => setOpen((o) => !o)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-noir border-b border-white/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-lg font-medium text-white/85"
                >
                  {l.label}
                </button>
              ))}
              <div className="pt-2"><AuthMenu mobile /></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
